import { UserService } from 'src/app/services/user.service';
import { Injectable } from '@angular/core';
import { Observable, of, forkJoin, BehaviorSubject, Subject } from 'rxjs';
import {
  OrganizationFeatureDtoResponse,
  OrganizationFeatureListResponseDto,
} from '../page/organization/models/organization-features';
import { DSRESTClient } from '../shared/communication-objects/restclient.service';
import { api } from '../shared/communication-objects/api-path';
import { LogService } from './log.service';
import { map, take, tap } from 'rxjs/operators';
import { Cacheable, CacheBuster, globalCacheBusterNotifier } from 'ts-cacheable';


const featureCacheBuster$ = new BehaviorSubject<void>(null);
@Injectable({
  providedIn: 'root',
})
export class OrganizationFeaturesService {
  private readonly apiUrl = api`organization-features`;

  constructor(
    private logger: LogService,
    private restClient: DSRESTClient,
    private userService: UserService,
  ) {}

  setPremiumServices(
    organizationId: number,
    value: boolean,
  ): Observable<boolean> {
    return this.restClient
      .patch<boolean>(
        api`${this.apiUrl}/set-premium-services/${organizationId}/${value}`,
      )
      .pipe(
        tap((response) =>
          this.logger.log('setPremiumServices Response', response),
        ),
      );
  }
  @Cacheable(
    {
      cacheBusterObserver: featureCacheBuster$,
    }
  )
  getOrganizationFeatures(
    organizationId: number,
  ): Observable<OrganizationFeatureDtoResponse> {
    return this.restClient
      .getDirect<OrganizationFeatureDtoResponse>(
        api`${this.apiUrl}/features/${organizationId}`,
      )
      .pipe(
        tap((response) =>
          this.logger.log('getOrganizationFeatures Response', response),
        ),
      );
  }

  @Cacheable(
    {
      cacheBusterObserver: featureCacheBuster$,
    }
  )
  getAllFeatures(
    organizationId: number,
  ): Observable<OrganizationFeatureListResponseDto> {
    return this.restClient
      .getDirect<OrganizationFeatureListResponseDto>(
        api`${this.apiUrl}/get-features/${organizationId}`,
      )
      .pipe(
        tap((response) => this.logger.log('getAllFeatures Response', response)),
      );
  }

  getPremiumUsers(organizationId: number): Observable<any> {
    // return a test list of premium users
    //return of([ { userId: 1, displayName: 'John Doe', isGroup: false }, { userId: 2, displayName: 'Jane Doe', isGroup: false } ]);
    return forkJoin({
      users: this.restClient.getDirect<any>(
        api`${this.apiUrl}/get-feature-managers-users/${organizationId}`,
      ),
      groups: this.restClient.getDirect<any>(
        api`${this.apiUrl}/get-feature-managers-groups/${organizationId}`,
      ),
    })
      .pipe(
        map((results) => results.users.concat(results.groups)),
        take(1),
      )
      .pipe(
        tap((response) =>
          this.logger.log('getPremiumUsers Response', response),
        ),
      );
  }

  setPremiumUsers(organizationId: number, premiumUsers: any): Observable<any> {
    globalCacheBusterNotifier.next();
    return this.restClient.post(
      api`${this.apiUrl}/set-feature-managers/${organizationId}`,
      premiumUsers.map((user) => user.userId),
    );
  }

  @Cacheable(
    {
      cacheBusterObserver: featureCacheBuster$
    }
  )
  getAvailablePremiumUsers(organizationId: number) {
    //join the two lists of users and groups
    return forkJoin({
      users: this.restClient.getDirect<any>(
        api`${this.apiUrl}/get-available-feature-managers-users/${organizationId}`,
      ),
      groups: this.restClient.getDirect<any>(
        api`${this.apiUrl}/get-available-feature-managers-groups/${organizationId}`,
      ),
    })
      .pipe(map((results) => results.users.concat(results.groups), take(1)))
      .pipe(
        tap((response) =>
          this.logger.log('getAvailablePremiumUsers Response', response),
        ),
      );
  }

  @CacheBuster({
    cacheBusterNotifier: featureCacheBuster$,
    isInstant: true,
  })
  setFeatures(
    organizationId: number,
    features: number[],
  ): Observable<number[]> {
    globalCacheBusterNotifier.next();
    return this.restClient
      .post(api`${this.apiUrl}/set-features/${organizationId}`, features)
      .pipe(
        tap((response) => this.logger.log('setFeatures Response', response)),
      );
  }

  @CacheBuster({
    cacheBusterNotifier: featureCacheBuster$,
    isInstant: true,
  })
  setOrganizationFeatures(
    organizationId: number,
    features: { [key: number]: boolean },
  ): Observable<any> {
    globalCacheBusterNotifier.next();
    return this.restClient
      .post(api`${this.apiUrl}/features/${organizationId}`, features)
      .pipe(
        tap((response) =>
          this.logger.log('setOrganizationFeatures Response', response),
        ),
      );
  }
}
